/**
 * @file components/PageTitle 一级标题组件
 * @author yangxingqiang
 */

import _ from 'lodash';
import React, {FC, ReactNode, useCallback} from 'react';
import {Button, Tooltip} from 'acud';
import {OutlinedLeft} from 'acud-icon';
import * as tmp from 'react-router-dom';
import classnames from 'classnames';
import {ButtonType} from 'acud/lib/button/button';

import {ACUD_PRO_PREFIX} from '../../../_common/constants';

import './index.less';

// ignore waring `"export 'useNavigate' (imported as 'rc') was not found in 'react-router-dom'`
const rc = tmp as any;
const {Link, generatePath} = tmp;

export interface PageTitleBtnProps {
    id?: string; // ID
    type?: ButtonType; // 同acud的Tooltip组件的type属性
    disabled?: boolean; // 不可点击
    text: ReactNode; // 按钮文本
    tooltip?: any; // hover提示，同acud的Tooltip组件的TooltipProps一致
    className?: string; // 类名
    btnAction?: () => void // 操作方法
}
export interface PageTitleProps {
    title?: ReactNode; // 标题
    backText?: string; // 返回的文本
    backUrl?: string | object & {pathname: string}; // 返回的url
    defaultBackUrl?: string;
    backParams?: any; // 返回的参数
    className?: string; // class名称
    custom?: ReactNode; // 自定义title位置的文本
    extra?: ReactNode; // title右侧扩展信息
    btnConfig?: PageTitleBtnProps[] // 按钮配置
    description?: ReactNode; // 换行的描述信息
}

const PageTitle: FC<PageTitleProps> = props => {
    const {
        defaultBackUrl,
        backUrl,
        backParams,
        backText = '返回',
        title,
        btnConfig,
        custom,
        className,
        extra,
        description
    } = props;

    // PageTitle组件通用前缀
    const pageTitlePrefix = `${ACUD_PRO_PREFIX}-base-page-title`;

    const hooksHistory = rc.useHistory?.();
    const navigate = rc.useNavigate?.();
    const history = hooksHistory || navigate;

    let backLink;
    if (_.isString(backUrl)) {
        backLink = backParams ? generatePath(backUrl, backParams) : backUrl;
    }
    else if (backUrl) {
        let pathname = _.get(backUrl, 'pathname');
        backLink = {
            ...(backUrl || {}),
            pathname: backParams ? generatePath(pathname, backParams) : pathname
        };
    }

    const handleAction = useCallback((btnItem: PageTitleBtnProps) => {
        const {btnAction} = btnItem;
        btnAction && btnAction();
    }, []);

    // 2. 自定义类名
    const prefixClass = classnames(`${pageTitlePrefix}-wrap`, className);

    // 3. 处理返回
    const handleBack = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('goBack') === 'default' && defaultBackUrl) {
            history.push(defaultBackUrl);
        }
        else {
            history.goBack();
        }
    }, [defaultBackUrl, history]);

    return (
        <div className={prefixClass}>
            <div className={pageTitlePrefix}>
                {/* 优先使用backUrl跳转，其次是defaultBackUrl，最后是浏览器返回上一页 */}
                {backUrl ? (
                    <div className="back-btn">
                        <Link to={backLink as any}>
                            <OutlinedLeft />
                            <span className="back-text">{backText}</span>
                        </Link>
                    </div>
                ) : defaultBackUrl ? (
                    <div className="back-btn" onClick={handleBack}>
                        <a>
                            <OutlinedLeft />
                            <span className="back-text">{backText}</span>
                        </a>
                    </div>
                ) : null}
                {title && (
                    <div className={`${pageTitlePrefix}-text ${!(backUrl || defaultBackUrl) && 'noBack'}`}>{title}</div>
                )}
                {custom && (
                    <div className={`${pageTitlePrefix}-custom`}>{custom}</div>
                )}
                {btnConfig && (
                    <div className={`${pageTitlePrefix}-btns-area`}>
                        {_.map(btnConfig, (btnItem, index) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const {disabled, text, tooltip, className, btnAction, ...otherProps} = btnItem;
                            const classNames = classnames(`${pageTitlePrefix}-btns-item`, className);
                            // 生成唯一Key
                            const uniqKey = btnItem.id || index;
                            const btnComp = (
                                <Button
                                    key={uniqKey}
                                    {...otherProps}
                                    className={classNames}
                                    disabled={!!disabled}
                                    onClick={() => handleAction(btnItem)}
                                >
                                    {text}
                                </Button>
                            );
                            return tooltip ? (
                                <Tooltip
                                    key={uniqKey}
                                    {...tooltip}
                                >
                                    {btnComp}
                                </Tooltip>
                            ) : btnComp;
                        })}
                    </div>
                )}
                {extra && <div className={`${pageTitlePrefix}-extra-area`}>{extra}</div>}
            </div>
            {description && <div className={`${pageTitlePrefix}-description`}>{description}</div>}
        </div>
    );
};

export default PageTitle;
